import React, { useState, useEffect, useCallback, useRef, useMemo} from 'react';
import { NotificationProvider } from './Notifications';
import { CPRLogProvider } from './CPRLog';
import { CPRStateProvider, useCPRState } from './CprState/CPRStateContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CPRSettingsProvider from './CPRSettings';
import { CPRState } from './CprState/types';
import { ChevronUp, ChevronDown } from 'lucide-react';
import {
  faFileLines,
  faSection,
  faPills,
  faListCheck,
  faLungs,
  faBedPulse,
  faPaperclip,
  faVials,
  faTruckMedical,
  faBoltLightning,
  faClockRotateLeft
} from '@fortawesome/free-solid-svg-icons';
import CPRLogComponent from './CPRLog';
import VitalSigns from './VitalSigns';
import MedicalProcedures from './MedicalProcedures';
import ABCDEFProcedures from './ABCDEFProcedures';
import Airways from './Airways';
import Medications from './Medications';
import Defibrillator from './Defibrillator';
import ReminderBox from './ReminderBox';
import { AbcdefReminder } from './Reminders';
import OpeningConditions from './OpeningConditions';
import { ResusProvider, useResusContext } from '../Resus/ResusContext';
import { loadCurrentState, saveCurrentState, restoreArchivedCPR, clearCurrentState } from './CprState/storage';
import Protocols from './Protocols';
import ArchivesList, { getProtocolName } from './ArchivesList';
import { cprEventEmitter, EVENTS } from './cprEvents';
import ControlPanel from './ControlPanel';
import './Cpr.scss';
import { getHospitalData } from './PerHospitalResusConfig';
import SecondarySurvey from './SecondarySurvey';
import RecommendedValuesPerAge from './RecommendedValuesPerAge';
import { useTranslation } from 'react-i18next';
import AppsHeader from '../components/AppsHeader';

interface CprProps {
  hospital: string;
  isNursingView: boolean;
}

interface CprContentProps {
  isNursingView: boolean;
}

type SectionName = 'openingConditions' | 'airways' | 'defibrillator' | 'vitalSigns' | 
                   'procedures' | 'secondarySurvey' | 'medications' | 'abcdef' |
                   'protocols' | 'log' | 'recommendedValuesPerAge';
const CprContent: React.FC<CprContentProps> = ({ isNursingView }) => {
  const { updateContext, protocols, resetContext, weight, hospital } = useResusContext();
  const emergencyProtocols = getHospitalData(hospital).protocols;
  const { t } = useTranslation(['resus']);
  
  const { dispatch } = useCPRState();
  const hasRestoredState = useRef(false);
  const sectionsRef = useRef<Record<SectionName, HTMLDivElement | null>>({
    openingConditions: null,
    airways: null,
    defibrillator: null,
    vitalSigns: null,
    procedures: null,
    secondarySurvey: null,
    medications: null,
    abcdef: null,
    protocols: null,
    log: null,
    recommendedValuesPerAge: null
  });
  
  // State management
  const [expandedSections, setExpandedSections] = useState<Record<SectionName, boolean>>({
    openingConditions: false,
    airways: false,
    defibrillator: false,
    vitalSigns: false,
    procedures: false,
    secondarySurvey: false,
    medications: false,
    abcdef: false,
    protocols: false,
    log: false,
    recommendedValuesPerAge: false
  });
  
  const [showReminder, setShowReminder] = useState(false);
  const [reminderShownForCurrentOpen, setReminderShownForCurrentOpen] = useState(false);
  const [showArchives, setShowArchives] = useState(false);  
  const [procedureStarted, setProcedureStarted] = useState(false);  

  const isInitialSetupComplete = useMemo(() => !!weight, [weight]);

  // Measure section heights for smooth animations
  const measureSectionHeight = useCallback((name: SectionName) => {
    const section = sectionsRef.current[name];
    if (section) {
      // Get the content height
      const height = section.scrollHeight;
      section.style.setProperty('--section-height', `${height}px`);
      setTimeout(function() {
        // remove the height, so each sub-component will be able to resize itself.
        section.style.setProperty('--section-height', '');
      }, 1000);      
    }
  }, []);

  const closeOpeningConditionsSection = () => {
    setExpandedSections(prev => ({
      ...prev,
      openingConditions: false
    }));
  }

  const handleOpeningConditionsUpdate = useCallback(closeOpeningConditionsSection, []);

  const initialPatientInputSubmitted = useCallback(() => {
    closeOpeningConditionsSection();
    const stateUpdates: Partial<CPRState> = {                    
      startTime: Date.now()
    };    
    saveCurrentState(stateUpdates);    
  }, []);

  // Handle state restoration
  useEffect(() => {
    const savedState = loadCurrentState();
    setProcedureStarted(!!savedState?.startTime);
    if (!hasRestoredState.current) {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
      const archiveId = params.get('archive');

      if (archiveId) {
        const restored = restoreArchivedCPR(archiveId);
        if (!restored) {
          console.error('Failed to restore archived CPR');
        }
      }      
      if (savedState?.resusContext) {
        const { age, weight, protocols } = savedState.resusContext;        
        if (age || weight || protocols) {
          updateContext(age === undefined ? null : age, weight || null, protocols || []);
        }
      }
      hasRestoredState.current = true;
    }
  }, [updateContext]);

  // Handle medication section opening
  useEffect(() => {
    const handleOpenMedications = () => {
      setExpandedSections(prev => ({ ...prev, medications: true }));
      setTimeout(() => {
        measureSectionHeight('medications');
        const medsSection = document.getElementById('medications');
        if (medsSection) {
          medsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    };

    const unsubscribe = cprEventEmitter.subscribe(EVENTS.OPEN_MEDICATIONS, handleOpenMedications);
    return () => unsubscribe();
  }, [measureSectionHeight]);

  useEffect(() => {
    const handleOpenMedications = (sectionName? : string) => {
      setExpandedSections(prev => ({ ...prev, medications: true }));
      setTimeout(() => {
        if (!sectionName) {
          return;
        }
        measureSectionHeight('medications');
        const medsSection = document.getElementById(`section-${sectionName}`);
        if (medsSection) {
          medsSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    };

    const unsubscribe = cprEventEmitter.subscribe(EVENTS.ADD_MEDS_SECTION, handleOpenMedications);
    return () => unsubscribe();
  }, [measureSectionHeight]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (expandedSections.abcdef) {
      // Reset the reminder shown state when section opens
      setReminderShownForCurrentOpen(false);
      
      // Set timer for showing reminder
      if (!reminderShownForCurrentOpen) {
        timer = setTimeout(() => {
          setShowReminder(true);
          setReminderShownForCurrentOpen(true);
        }, 3000);
      }
    } else {
      // When section closes, reset reminder state
      setShowReminder(false);
    }
  
    return () => clearTimeout(timer);
  }, [expandedSections.abcdef, reminderShownForCurrentOpen]);

  const getHeaderTitle = (protocolIds: string[]): string => {
    if (protocolIds.length != 1)
    {
      return t('system.title');
    }
    return getProtocolName(emergencyProtocols, protocolIds[0]);
  };

  const sections: Record<SectionName, JSX.Element> = {
    openingConditions: (
      <OpeningConditions         
        onUpdate={handleOpeningConditionsUpdate}
        isNursingView={isNursingView}
      />
    ),
    airways: <Airways />,
    defibrillator: <Defibrillator />,
    vitalSigns: <VitalSigns />,
    procedures: <MedicalProcedures />,    
    medications: <Medications />,
    abcdef: <ABCDEFProcedures />,
    secondarySurvey: <SecondarySurvey />,
    protocols: <Protocols />,
    log: <CPRLogComponent />,
    recommendedValuesPerAge: <RecommendedValuesPerAge />
  };

  const handleResetAll = useCallback(() => {
    clearCurrentState();    
    dispatch({ 
      type: 'LOAD_STATE', 
      state: {
        isRunning: false,
        startTime: 0,
        lastAdrenalineTime: 0,
        lastMassagerTime: 0,
        adrenalineCount: 0,
        shockCount: 0,
        status: null,
        endTime: null
      }
    });
    cprEventEmitter.emit(EVENTS.RESET_CPR);
    resetContext();
    setShowReminder(false);
    setReminderShownForCurrentOpen(false);
    setProcedureStarted(false);
    setExpandedSections({
      openingConditions: false,
      airways: false,
      defibrillator: false,
      vitalSigns: false,
      procedures: false,
      secondarySurvey: false,
      medications: false,
      abcdef: false,
      protocols: false,
      log: false,
      recommendedValuesPerAge: false
    });
  }, [dispatch, resetContext]);

  const toggleSection = useCallback((sectionName: SectionName) => {
    if (!expandedSections[sectionName]) {
      measureSectionHeight(sectionName);
      
      // Wait for expansion animation to complete before scrolling
      setTimeout(() => {
        const sectionHeader = sectionsRef.current[sectionName]?.previousElementSibling;
        if (sectionHeader) {
          sectionHeader.scrollIntoView({ 
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 300); // Match the duration-300 class timing
    }
    
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  }, [expandedSections, measureSectionHeight]);

  const renderSection = useCallback((name: SectionName, icon: any, title: string) => {
    if (!isInitialSetupComplete) return null;

    const collapseSection = () => {
      setExpandedSections(prev => ({
        ...prev,
        [name]: false
      }));
    };
    
    return (
      <div 
        className="transition-all duration-300 section-container" 
        style={{ direction: 'rtl' }}
      >        
        <h4 
          className={`section-header ${expandedSections[name] ? 'expanded' : ''}`} 
          onClick={() => toggleSection(name)}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              toggleSection(name);
            }
          }}
        >
          <span className="section-name">
            <FontAwesomeIcon style={{ marginLeft: '8px' }} icon={icon} /> {title}
          </span>
          <span className="toggle-icon">
            {expandedSections[name] ? 
              <ChevronUp size={20} strokeWidth={2.5}/> : 
              <ChevronDown size={20} strokeWidth={2.5}/>
            }
          </span>
        </h4>
        <div 
          ref={el => sectionsRef.current[name] = el}
          className={`collapsible-section ${
            expandedSections[name] ? 'expanded' : 'collapsed'
          }`}
        >
          <div className="section-content">
            {/* Pass the collapse function if this is the Airways section */}
            {name === 'airways' 
              ? React.cloneElement(sections[name], { onCollapse: collapseSection })
              : sections[name]
            }
          </div>
        </div>
      </div>
    );
  }, [expandedSections, isInitialSetupComplete, sections, toggleSection]);

  return (
    <div className="cpr container main-content" id="cpr-main">
      <div className="group-container">
          <div style={{ position: 'relative' }}>
          <AppsHeader
            title={getHeaderTitle(protocols)}
          />
          {!procedureStarted && (
          <div style={{ position: 'absolute', left: 0, top: 0 }}>
            <button
              onClick={() => setShowArchives(true)}
              className="archive-button"
              title={t('buttons.previousProcedures')}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} />
            </button>
          </div>
          )}
        </div>

        <ControlPanel 
        isNursingView={isNursingView}
        onResetAll={handleResetAll}
        onOpenPatientInfo={() => {
          setExpandedSections(prev => ({
            ...prev,
            openingConditions: true
          }));
          setTimeout(() => {
            const sectionHeader = sectionsRef.current['openingConditions']?.previousElementSibling;
            if (sectionHeader) {
              sectionHeader.scrollIntoView({ 
                behavior: 'smooth',
                block: 'start'
              });
            }
          }, 300);
        }}
      />
        {!isInitialSetupComplete ? (
          <div className="opening-conditions-initial-setup-container">            
            <OpeningConditions 
              onUpdate={initialPatientInputSubmitted}
              isNursingView={isNursingView}
            />
          </div>
        ) : (
          <div className="sections-container">
            <div className="sections-list-container">
              {isNursingView ? (
                <>
                  {renderSection('openingConditions', faTruckMedical, t('menu.opening'))}
                  {renderSection('protocols', faPaperclip, t('menu.protocols'))}
                  {renderSection('medications', faPills, t('menu.medications'))}
                  {renderSection('recommendedValuesPerAge', faSection, t('menu.recommendedValuesForNursing'))}
                </>
              ) : (
                <>
                  {renderSection('openingConditions', faTruckMedical, t('menu.opening'))}
                  {renderSection('airways', faLungs, t('menu.airways'))}
                  {renderSection('defibrillator', faBoltLightning, t('menu.defibrillator'))}
                  {renderSection('vitalSigns', faBedPulse, t('menu.vitalSigns'))}
                  {renderSection('procedures', faSection, t('menu.procedures'))}
                  {renderSection('medications', faPills, t('menu.medications'))}
                  {renderSection('abcdef', faListCheck, t('menu.abcdef'))}
                  {renderSection('secondarySurvey', faVials, t('menu.secondarySurvey'))}
                  {renderSection('protocols', faPaperclip, t('menu.protocols'))}
                  {renderSection('log', faFileLines, t('menu.log'))}
                </>
              )}
            </div>
          </div>
        )}

        {showArchives && <ArchivesList onClose={() => setShowArchives(false)} />}
        {showReminder && (
          <ReminderBox 
            content={<AbcdefReminder />}
            vibrateDelay={2000} // Optional, this is the default value
            duration={6000}     // Optional, this is the default value
          />
        )}
      </div>
    </div>
  );
};

const Cpr: React.FC<CprProps> = ({ hospital, isNursingView }) => {
  return (
    <ResusProvider hospital={hospital}>
      <CPRSettingsProvider>
        <NotificationProvider>
          <CPRLogProvider>
            <CPRStateProvider>
              <CprContent isNursingView={isNursingView} />
            </CPRStateProvider>
          </CPRLogProvider>
        </NotificationProvider>
      </CPRSettingsProvider>
    </ResusProvider>
  );
};

export default Cpr;